// src/components/ao/WorkflowModals.jsx

import React, { useState, useEffect } from 'react';
import { X, UserCheck, Users, Calendar, MessageSquare, AlertCircle, Play } from 'lucide-react';
import api, { buAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const extractError = (error) => {
  const toText = (value) => {
    if (value === null || value === undefined) return '';

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => toText(item))
        .filter(Boolean)
        .join('\n');
    }

    if (typeof value === 'object') {
      if (value.msg) {
        const loc = Array.isArray(value.loc)
          ? value.loc.join(' > ')
          : value.loc;

        return loc ? `${loc} : ${value.msg}` : String(value.msg);
      }

      if (value.detail) return toText(value.detail);
      if (value.message) return String(value.message);
      if (value.error) return String(value.error);

      try {
        return JSON.stringify(value);
      } catch {
        return 'Erreur non lisible';
      }
    }

    return String(value);
  };

  const data = error?.response?.data;

  if (data?.detail !== undefined) {
    return toText(data.detail) || 'Une erreur est survenue';
  }

  if (data !== undefined) {
    return toText(data) || 'Une erreur est survenue';
  }

  if (error?.message) {
    return String(error.message);
  }

  return 'Erreur';
};
// ─────────────────────────────────────────────────────────────
// Modal : Affecter un Ingénieur d'Affaires (Admin → IA)
// ─────────────────────────────────────────────────────────────
export function AssignIAModal({ ao, onClose, onSaved }) {
  const [saving,  setSaving]  = useState(false);
  const [iaList,  setIaList]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [form,    setForm]    = useState({
    handler_user_id:    '',
    shortlist_deadline: '',
    message:            '',
  });

  useEffect(() => {
    // Endpoint public pour lister les IA (accessible à tous les connectés)
    api.get('/admin/users/ta-list').then(r => {
      setIaList((r.data || []).filter(u =>
        (u.role === 'BUSINESS_MANAGER') && u.is_active
      ));
    }).catch(() => {
      // Fallback : /admin/users si accessible
      api.get('/admin/users').then(r => {
        setIaList((r.data || []).filter(u => u.role === 'BUSINESS_MANAGER' && u.is_active));
      }).catch(() => {});
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.handler_user_id) { toast.error("Sélectionnez un Ingénieur d'Affaires"); return; }
    setSaving(true);
    try {
      await api.post(`/ao-management/${ao.ao_id || ao.id}/assign-ia`, {
        handler_user_id:    form.handler_user_id,
        shortlist_deadline: form.shortlist_deadline || undefined,
        message:            form.message || undefined,
      });
      toast.success("AO affecté à l'Ingénieur d'Affaires");
      onSaved();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Affecter à un Ingénieur d'Affaires" icon={<UserCheck size={18} color="#FF4100"/>} onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:16, padding:'4px 0' }}>
        <div>
          <label style={lbl}>Ingénieur d'Affaires *</label>
          {loading ? (
            <div style={{ display:'flex', gap:8, color:'var(--text-muted)', padding:'10px 0' }}>
              <div className="spinner" style={{ width:14, height:14 }}/> Chargement…
            </div>
          ) : iaList.length === 0 ? (
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:'rgba(239,68,68,0.12)', borderRadius:8, color:'#DC2626', fontSize:'0.875rem' }}>
              <AlertCircle size={14}/> Aucun Ingénieur d'Affaires disponible
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {iaList.map(u => (
                <label key={u.id} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                  border:`2px solid ${form.handler_user_id === u.id ? '#FF4100' : 'var(--border)'}`,
                  borderRadius:10, cursor:'pointer',
                  background: form.handler_user_id === u.id ? 'var(--orange-50)' : 'var(--bg-elevated)',
                  transition:'all 0.15s',
                }}>
                  <input type="radio" name="ia" value={u.id} checked={form.handler_user_id === u.id}
                    onChange={() => setForm(f => ({ ...f, handler_user_id: u.id }))}
                    style={{ display:'none' }}/>
                  <div className="avatar avatar-sm" style={{ flexShrink:0, borderRadius:8,
                    background: form.handler_user_id === u.id ? '#FF4100' : 'var(--border)',
                    color: form.handler_user_id === u.id ? 'white' : 'var(--text-muted)' }}>
                    {(u.prenom?.[0]||'')+(u.nom?.[0]||'')}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{u.prenom} {u.nom}</div>
                    <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{u.email}</div>
                  </div>
                  {form.handler_user_id === u.id && (
                    <div style={{ width:20, height:20, borderRadius:'50%', background:'#FF4100', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <span style={{ color:'white', fontSize:'0.75rem', fontWeight:700 }}>✓</span>
                    </div>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        <div>
          <label style={lbl}><Calendar size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Délai de traitement (optionnel)</label>
          <input style={inp} type="date" value={form.shortlist_deadline}
            onChange={e => setForm(f => ({ ...f, shortlist_deadline: e.target.value }))}/>
          <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:4 }}>Date limite pour finaliser ce dossier</div>
        </div>

        <div>
          <label style={lbl}><MessageSquare size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Message (optionnel)</label>
          <textarea style={{ ...inp, minHeight:80, resize:'vertical' }}
            placeholder="Instructions particulières pour l'Ingénieur d'Affaires…"
            value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}/>
        </div>
      </div>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving}
        saveLabel="Affecter l'AO" saveColor="#FF4100" disabled={!form.handler_user_id}/>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal : Envoyer au Talent Acquisition
// Permet d'envoyer à TOUTE L'ÉQUIPE ou à un TA SPÉCIFIQUE
// ─────────────────────────────────────────────────────────────
export function SendToTAModal({ ao, onClose, onSaved }) {
  const { user } = useAuth();
  const isCommercial = (user?.role || '').toLowerCase() === 'business_manager';

  const [saving,  setSaving]  = useState(false);
  const [taList,  setTaList]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [buFiltered, setBuFiltered] = useState(false);
  const [mode,    setMode]    = useState('equipe'); // 'equipe' | 'specifique'
  const [form,    setForm]    = useState({
    talent_acquisition_id: '',
    shortlist_deadline:    '',
    message:               '',
  });

  useEffect(() => {
    const loadTAs = async () => {
      // Commercial → on essaie d'abord les TAs de sa BU
      if (isCommercial) {
        try {
          const r = await buAPI.getMyBUTAUsers();
          const buTAs = (r.data || []).filter(u => u.is_active !== false);
          if (buTAs.length > 0) {
            setTaList(buTAs);
            setBuFiltered(true);
            setLoading(false);
            return;
          }
        } catch {}
      }
      // Fallback : toute la liste
      api.get('/admin/users/ta-list').then(r => {
        setTaList((r.data || []).filter(u => u.role === 'TALENT_ACQUISITION' && u.is_active));
      }).catch(() => {
        api.get('/admin/users').then(r => {
          setTaList((r.data || []).filter(u => u.role === 'TALENT_ACQUISITION' && u.is_active));
        }).catch(() => {});
      }).finally(() => setLoading(false));
    };
    loadTAs();
  }, [isCommercial]);

  const handleSave = async () => {
    if (mode === 'specifique' && !form.talent_acquisition_id) {
      toast.error('Sélectionnez un Talent Acquisition'); return;
    }
    setSaving(true);
    try {
      if (mode === 'equipe') {
        // Envoyer à toute l'équipe TA — pas de TA spécifique
        await api.post(`/ao-management/${ao.ao_id || ao.id}/assign-talent`, {
          talent_acquisition_id: null,
        });
        if (form.shortlist_deadline || form.message) {
          // Mettre à jour le délai si fourni
          await api.put(`/ao-management/${ao.ao_id || ao.id}`, {
            response_deadline: form.shortlist_deadline || undefined,
          });
        }
        toast.success('AO envoyé à toute l\'équipe Talent Acquisition');
      } else {
        // Envoyer à un TA spécifique avec délai
        await api.post(`/ao-management/${ao.ao_id || ao.id}/send-to-ta-with-deadline`, {
          talent_acquisition_id: form.talent_acquisition_id,
          shortlist_deadline:    form.shortlist_deadline || undefined,
          message:               form.message || undefined,
        });
        toast.success('AO envoyé au Talent Acquisition');
      }
      onSaved();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const canSave = mode === 'equipe' || (mode === 'specifique' && !!form.talent_acquisition_id);




  

  return (
    <Modal title="Envoyer au Talent Acquisition" icon={<Users size={18} color="#8B5CF6"/>} onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:16, padding:'4px 0' }}>

        {/* Recap AO */}
        <div style={{ padding:'12px 14px', background:'rgba(139,92,246,0.10)', borderRadius:10, border:'1px solid #DDD6FE' }}>
          <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#7C3AED' }}>{ao.job_title || 'AO sans titre'}</div>
          {ao.client && <div style={{ fontSize:'0.75rem', color:'#6D28D9', marginTop:2 }}>{ao.client}</div>}
        </div>

        {buFiltered && (
          <div style={{ padding:'8px 12px', background:'var(--orange-50)', border:'1px solid rgba(255,65,0,0.22)', borderRadius:8, fontSize:'0.8125rem', color:'#FF4100', display:'flex', alignItems:'center', gap:6 }}>
            <Users size={13}/> Affichage limité aux Talent Acquisition de votre BU
          </div>
        )}

        {/* Choix mode : équipe ou TA spécifique */}
        <div>
          <label style={lbl}>Envoyer à *</label>
          <div style={{ display:'flex', gap:8 }}>
            {[
              { key:'equipe',     label:'Toute l\'équipe TA', desc:'Tous les TA verront cet AO et pourront le prendre' },
              { key:'specifique', label:'Un TA spécifique',   desc:'Seul ce TA sera notifié et assigné' },
            ].map(opt => (
              <button key={opt.key} type="button" onClick={() => { setMode(opt.key); setForm(f => ({ ...f, talent_acquisition_id: '' })); }}
                style={{ flex:1, padding:'12px 14px', borderRadius:10, cursor:'pointer',
                  border:`2px solid ${mode === opt.key ? '#A78BFA' : 'var(--border)'}`,
                  background: mode === opt.key ? 'rgba(139,92,246,0.10)' : 'var(--bg-elevated)',
                  textAlign:'left', transition:'all 0.15s' }}>
                <div style={{ fontWeight:700, fontSize:'0.875rem', color: mode === opt.key ? '#7C3AED' : 'var(--text-primary)' }}>
                  {opt.label}
                </div>
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:3, lineHeight:1.4 }}>
                  {opt.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Sélection TA spécifique */}
        {mode === 'specifique' && (
          <div>
            <label style={lbl}>Talent Acquisition *</label>
            {loading ? (
              <div style={{ display:'flex', gap:8, color:'var(--text-muted)', padding:'10px 0' }}>
                <div className="spinner" style={{ width:14, height:14 }}/> Chargement…
              </div>
            ) : taList.length === 0 ? (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:'rgba(239,68,68,0.12)', borderRadius:8, color:'#DC2626', fontSize:'0.875rem' }}>
                <AlertCircle size={14}/> Aucun Talent Acquisition disponible
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:200, overflowY:'auto' }}>
                {taList.map(u => (
                  <label key={u.id} style={{
                    display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                    border:`2px solid ${form.talent_acquisition_id === u.id ? '#A78BFA' : 'var(--border)'}`,
                    borderRadius:10, cursor:'pointer',
                    background: form.talent_acquisition_id === u.id ? 'rgba(139,92,246,0.10)' : 'var(--bg-elevated)',
                    transition:'all 0.15s',
                  }}>
                    <input type="radio" name="ta" value={u.id} checked={form.talent_acquisition_id === u.id}
                      onChange={() => setForm(f => ({ ...f, talent_acquisition_id: u.id }))}
                      style={{ display:'none' }}/>
                    <div className="avatar avatar-sm" style={{ flexShrink:0, borderRadius:8,
                      background: form.talent_acquisition_id === u.id ? '#A78BFA' : 'rgba(255,255,255,0.10)',
                      color: form.talent_acquisition_id === u.id ? 'white' : 'var(--text-muted)' }}>
                      {(u.prenom?.[0]||'')+(u.nom?.[0]||'')}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{u.prenom} {u.nom}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{u.email}</div>
                    </div>
                    {form.talent_acquisition_id === u.id && (
                      <div style={{ width:20, height:20, borderRadius:'50%', background:'#A78BFA', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <span style={{ color:'white', fontSize:'0.75rem', fontWeight:700 }}>✓</span>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Délai shortlist */}
        <div>
          <label style={lbl}><Calendar size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Délai pour envoyer la shortlist</label>
          <input style={{ ...inp, borderColor: form.shortlist_deadline ? '#A78BFA' : 'var(--border)' }}
            type="date" value={form.shortlist_deadline}
            onChange={e => setForm(f => ({ ...f, shortlist_deadline: e.target.value }))}/>
          <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:4 }}>
            Visible par le TA dans son tableau de bord
          </div>
        </div>

        {/* Message */}
        <div>
          <label style={lbl}><MessageSquare size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Message (optionnel)</label>
          <textarea style={{ ...inp, minHeight:80, resize:'vertical' }}
            placeholder="Contexte, priorités, critères importants pour ce sourcing…"
            value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}/>
        </div>
      </div>

      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving}
        saveLabel={mode === 'equipe' ? 'Envoyer à l\'équipe TA' : 'Envoyer au TA'}
        saveColor="#8B5CF6" disabled={!canSave}/>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
// Composants partagés
// ─────────────────────────────────────────────────────────────
function Modal({ title, icon, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2500, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg-card)', borderRadius:16, width:'100%', maxWidth:520,
        maxHeight:'90vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'18px 24px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'var(--bg-elevated)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {icon}
          </div>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, flex:1 }}>{title}</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onClose, onSave, saving, saveLabel, saveColor = '#FF4100', disabled = false }) {
  return (
    <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:16, marginTop:8, borderTop:'1px solid var(--border)' }}>
      <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
      <button onClick={onSave} disabled={saving || disabled}
        style={{ display:'inline-flex', alignItems:'center', gap:6,
          padding:'9px 20px', border:'none', borderRadius:8,
          background: disabled ? 'var(--border)' : saveColor,
          color: disabled ? 'var(--text-muted)' : 'white',
          fontWeight:700, fontSize:'0.875rem',
          cursor: disabled ? 'not-allowed' : 'pointer', transition:'all 0.15s' }}>
        {saving ? <><div className="spinner" style={{ width:13, height:13, borderTopColor:'white' }}/> Envoi…</> : saveLabel}
      </button>
    </div>
  );
}

const lbl = {
  display:'block', fontSize:'0.8125rem', fontWeight:600,
  color:'var(--text-muted)', marginBottom:6,
  textTransform:'uppercase', letterSpacing:'0.05em',
};
const inp = {
  width:'100%', padding:'9px 12px', border:'1.5px solid var(--border)',
  borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box',
  fontFamily:'inherit', outline:'none', transition:'border-color 0.15s',
  background:'var(--bg-elevated)', color:'var(--text-primary)',
};


// ─────────────────────────────────────────────────────────────
// Modal : Prise en charge unifiée (IA + TA dans la même pop-up)
// ─────────────────────────────────────────────────────────────
export function AssignResponsableModal({ ao, currentUser, onClose, onSaved }) {
  const [saving,       setSaving]       = useState(false);
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedId,   setSelectedId]   = useState('');
  const [deadline,     setDeadline]     = useState('');
  const [message,      setMessage]      = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/admin/users/ta-list');
        setUsers((r.data || []).filter(u =>
          ['BUSINESS_MANAGER', 'TALENT_ACQUISITION'].includes(u.role) && u.is_active
        ));
      } catch {
        try {
          const r = await api.get('/admin/users');
          setUsers((r.data || []).filter(u =>
            ['BUSINESS_MANAGER', 'TALENT_ACQUISITION'].includes(u.role) && u.is_active
          ));
        } catch {}
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const iaUsers = users.filter(u => u.role === 'BUSINESS_MANAGER');
  const taUsers = users.filter(u => u.role === 'TALENT_ACQUISITION');
  const selectedUser = users.find(u => u.id === selectedId);

  const handleTakeMyself = async () => {
    setSaving(true);
    try {
      await api.post(`/ao-management/${ao.ao_id || ao.id}/take`);
      toast.success('Vous avez pris en charge cet AO');
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedId) { toast.error('Sélectionnez une personne'); return; }
    setSaving(true);
    try {
      if (selectedUser?.role === 'BUSINESS_MANAGER') {
        await api.post(`/ao-management/${ao.ao_id || ao.id}/assign-ia`, {
          handler_user_id:    selectedId,
          shortlist_deadline: deadline || undefined,
          message:            message || undefined,
        });
        toast.success("AO affecté à l'Ingénieur d'Affaires");
      } else {
        await api.post(`/ao-management/${ao.ao_id || ao.id}/send-to-ta-with-deadline`, {
          talent_acquisition_id: selectedId,
          shortlist_deadline:    deadline || undefined,
          message:               message || undefined,
        });
        toast.success('AO envoyé au Talent Acquisition');
      }
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const renderGroup = (label, list, color) => list.length > 0 && (
    <div>
      <div style={{ fontSize:'0.75rem', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.05em', margin:'10px 0 6px' }}>
        {label} ({list.length})
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {list.map(u => (
          <label key={u.id} style={{
            display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
            border:`2px solid ${selectedId === u.id ? color : 'var(--border)'}`,
            borderRadius:10, cursor:'pointer',
            background: selectedId === u.id ? `${color}18` : 'var(--bg-elevated)',
            transition:'all 0.15s',
          }}>
            <input type="radio" name="responsable" value={u.id} checked={selectedId === u.id}
              onChange={() => setSelectedId(u.id)} style={{ display:'none' }}/>
            <div className="avatar avatar-sm" style={{
              flexShrink:0, borderRadius:8,
              background: selectedId === u.id ? color : 'var(--border)',
              color: selectedId === u.id ? 'white' : 'var(--text-muted)',
            }}>
              {(u.prenom?.[0]||'')+(u.nom?.[0]||'')}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{u.prenom} {u.nom}</div>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{u.email}</div>
            </div>
            {selectedId === u.id && (
              <div style={{ width:20, height:20, borderRadius:'50%', background:color, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ color:'white', fontSize:'0.75rem', fontWeight:700 }}>✓</span>
              </div>
            )}
          </label>
        ))}
      </div>
    </div>
  );

  const canTakeMyself = currentUser && ['BUSINESS_MANAGER', 'TALENT_ACQUISITION', 'ADMIN', 'SUPER_ADMIN']
    .includes((currentUser.role || '').toUpperCase());

  return (
    <Modal title="Prise en charge de l'AO" icon={<UserCheck size={18} color="#FF4100"/>} onClose={onClose}>
      <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'4px 0' }}>

        <div style={{ padding:'12px 14px', background:'var(--orange-50)', borderRadius:10, border:'1px solid rgba(255,65,0,0.22)' }}>
          <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#FF4100' }}>{ao.job_title || 'AO sans titre'}</div>
          {ao.client && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>{ao.client}</div>}
        </div>

        {canTakeMyself && (
          <>
            <button type="button" onClick={handleTakeMyself} disabled={saving}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'11px 14px', borderRadius:10,
                border:'2px dashed #FF4100', background:'var(--orange-50)', cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight:700, fontSize:'0.875rem', color:'#FF4100', opacity: saving ? 0.6 : 1 }}>
              <Play size={14}/> Je prends en charge moi-même
            </button>
            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', textAlign:'center' }}>— ou affecter à —</div>
          </>
        )}

        <label style={lbl}>Ingénieurs d'Affaires &amp; Talent Acquisition</label>
        {loading ? (
          <div style={{ display:'flex', gap:8, color:'var(--text-muted)', padding:'10px 0' }}>
            <div className="spinner" style={{ width:14, height:14 }}/> Chargement…
          </div>
        ) : users.length === 0 ? (
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', background:'rgba(239,68,68,0.12)', borderRadius:8, color:'#DC2626', fontSize:'0.875rem' }}>
            <AlertCircle size={14}/> Aucun utilisateur disponible
          </div>
        ) : (
          <div style={{ maxHeight:280, overflowY:'auto' }}>
            {renderGroup("Ingénieurs d'Affaires", iaUsers, '#FF4100')}
            {renderGroup('Talent Acquisition', taUsers, '#A78BFA')}
          </div>
        )}

        <div>
          <label style={lbl}><Calendar size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Délai (optionnel)</label>
          <input style={inp} type="date" value={deadline} onChange={e => setDeadline(e.target.value)}/>
        </div>

        <div>
          <label style={lbl}><MessageSquare size={12} style={{ verticalAlign:'middle', marginRight:4 }}/>Message (optionnel)</label>
          <textarea style={{ ...inp, minHeight:70, resize:'vertical' }}
            placeholder="Instructions particulières…"
            value={message} onChange={e => setMessage(e.target.value)}/>
        </div>
      </div>

      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving}
        saveLabel={selectedUser ? `Affecter à ${selectedUser.prenom}` : 'Affecter'}
        saveColor={selectedUser?.role === 'TALENT_ACQUISITION' ? '#A78BFA' : '#FF4100'}
        disabled={!selectedId}/>
    </Modal>
  );
}